import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { CheckIcon, PencilSquareIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../../common/Button";
import Dropdown from "../../Settings/Dropdown";
import { AgencyTagRow, documentsApi } from "./api";
import { TAG_COLOR_DOTS, TAG_COLOR_NAMES, TagChip } from "./tagColors";

interface Props {
  open: boolean;
  onClose: () => void;
  tags: AgencyTagRow[];
  onChanged: () => Promise<void> | void;
}

// The agency's tag vocabulary: rename, recolor, describe, merge, delete, add.
const TagManagerModal: React.FC<Props> = ({ open, onClose, tags, onChanged }) => {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ name: string; description: string; color: string }>({ name: "", description: "", color: "blue" });
  const [merging, setMerging] = useState<string | null>(null);
  const [mergeInto, setMergeInto] = useState("");
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setEditing(null);
      setMerging(null);
      setNewName("");
    }
  }, [open]);

  if (!open) return null;

  const startEdit = (t: AgencyTagRow) => {
    setMerging(null);
    setEditing(t.id);
    setDraft({ name: t.name, description: t.description || "", color: t.color || "blue" });
  };

  const saveEdit = async (t: AgencyTagRow) => {
    setBusy(true);
    try {
      await documentsApi.updateTag(t.id, { name: draft.name.trim(), description: draft.description.trim() || null, color: draft.color });
      setEditing(null);
      await onChanged();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Could not update tag");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t: AgencyTagRow) => {
    if (!window.confirm(`Delete the tag "${t.name}"? It will be removed from ${t.sections} section${t.sections === 1 ? "" : "s"}.`)) return;
    setBusy(true);
    try {
      await documentsApi.deleteTag(t.id);
      await onChanged();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Could not delete tag");
    } finally {
      setBusy(false);
    }
  };

  const merge = async (t: AgencyTagRow) => {
    if (!mergeInto) return;
    setBusy(true);
    try {
      await documentsApi.mergeTag(t.id, mergeInto);
      toast.success(`Merged "${t.name}" into "${tags.find((x) => x.id === mergeInto)?.name}"`);
      setMerging(null);
      setMergeInto("");
      await onChanged();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Could not merge tags");
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    const name = newName.trim();
    if (name.length < 2) return;
    setBusy(true);
    try {
      await documentsApi.createTag({ name });
      setNewName("");
      await onChanged();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Could not create tag");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal modal-open" role="dialog">
      <div className="modal-box max-w-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-lg">Tags</h3>
            <p className="text-sm text-base-content/60 mt-0.5">The topics your sections are labeled with. The report writer uses them to pick which sections apply to an incident.</p>
          </div>
          <Button variant="clean" size="sm" className="px-1.5" onClick={onClose} title="Close">
            <XMarkIcon className="size-5" />
          </Button>
        </div>

        <form
          className="mt-4 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            create();
          }}
        >
          <input
            className="input input-bordered input-sm flex-1 bg-base-100 focus:outline-none focus:border-blue-500"
            placeholder="New tag, e.g. body worn camera"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <Button type="submit" variant="secondary" size="sm" disabled={busy || newName.trim().length < 2}>
            Add tag
          </Button>
        </form>

        <ul className="mt-4 divide-y divide-base-300 max-h-[60vh] overflow-auto -mx-2 px-2">
          {tags.map((t) => (
            <li key={t.id} className="py-2.5">
              {editing === t.id ? (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input className="input input-bordered input-sm flex-1 bg-base-100 focus:outline-none focus:border-blue-500" value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
                    <div className="flex items-center gap-1">
                      {TAG_COLOR_NAMES.map((c) => (
                        <button
                          key={c}
                          type="button"
                          title={c}
                          onClick={() => setDraft((d) => ({ ...d, color: c }))}
                          className={cn("size-5 rounded-full", TAG_COLOR_DOTS[c], draft.color === c ? "ring-2 ring-offset-2 ring-blue-500 ring-offset-base-100" : "opacity-70 hover:opacity-100")}
                        />
                      ))}
                    </div>
                  </div>
                  <input
                    className="input input-bordered input-sm w-full bg-base-100 focus:outline-none focus:border-blue-500"
                    placeholder="When does this tag apply? (helps the tagger)"
                    value={draft.description}
                    onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="xs" onClick={() => setEditing(null)}>
                      Cancel
                    </Button>
                    <Button variant="primary" size="xs" disabled={busy || draft.name.trim().length < 2} onClick={() => saveEdit(t)}>
                      <CheckIcon className="size-4 mr-1" /> Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <TagChip name={t.name} color={t.color} />
                      <span className="text-xs text-base-content/50">
                        {t.sections} section{t.sections === 1 ? "" : "s"}
                        {t.origin === "auto" ? " · added automatically" : ""}
                      </span>
                    </div>
                    {t.description && <p className="mt-1 text-xs text-base-content/60">{t.description}</p>}
                    {merging === t.id && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs text-base-content/70">Merge into</span>
                        <Dropdown
                          size="sm"
                          dense
                          menuAlign="start"
                          options={[{ value: "", label: "Pick a tag" }, ...tags.filter((x) => x.id !== t.id).map((x) => ({ value: x.id, label: x.name }))]}
                          value={mergeInto}
                          onChange={setMergeInto}
                        />
                        <Button variant="primary" size="xs" disabled={busy || !mergeInto} onClick={() => merge(t)}>
                          Merge
                        </Button>
                        <Button variant="outline" size="xs" onClick={() => setMerging(null)}>
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="clean" size="xs" className="px-1.5 hover:bg-base-200" title="Rename or recolor" onClick={() => startEdit(t)}>
                      <PencilSquareIcon className="size-4" />
                    </Button>
                    <Button
                      variant="clean"
                      size="xs"
                      className="px-1.5 hover:bg-base-200 text-xs"
                      title="Merge into another tag"
                      onClick={() => {
                        setEditing(null);
                        setMerging(merging === t.id ? null : t.id);
                        setMergeInto("");
                      }}
                    >
                      Merge
                    </Button>
                    <Button variant="clean" size="xs" className="px-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40" title="Delete" onClick={() => remove(t)}>
                      <TrashIcon className="size-4" />
                    </Button>
                  </div>
                </div>
              )}
            </li>
          ))}
          {tags.length === 0 && <li className="py-6 text-center text-sm text-base-content/60">No tags yet. They are created when the first document is tagged.</li>}
        </ul>
      </div>
      <div className="modal-backdrop" onClick={onClose} />
    </div>
  );
};

export default TagManagerModal;
